import { useReactiveVar } from "@apollo/client";
import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ApolloError from "../../../components/apolloError";
import InputText from "../../../components/inputs/inputText";
import ScrollToFieldError from "../../../components/ScrollToFieldError";
import CustomToast from "../../../components/tooltips/CustomToast";
import useGetUrlLocale from "../../../hooks/useGetUrlLocale";
import {
  whiteLabelJRActive,
  whiteLabelJRStep,
} from "../../../qraphql/globalVar/state";
import {
  useCreateOrUpdateWhiteLabelSocialLinksMutation,
  useOrganizerWhiteLabelQuery,
} from "../../../qraphql/whiteLabel/whiteLabel.hooks";
import { validationSchemaSocialLinks } from "../../../validations/whiteLabel";
import {
  InstagramIcon,
  FacebookIcon,
  XIcon,
  YoutubeIcon,
  LinkedinIcon,
  TiktokIcon,
} from "./svg";

interface ISocialLinksForm {
  instagram: string;
  facebook: string;
  x: string;
  youtube: string;
  linkedin: string;
  tiktok: string;
}

export default function SocialLinks() {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<ISocialLinksForm>>(null);
  const [
    createOrUpdateWhiteLabelSocialLinks,
    {
      loading: createOrUpdateWhiteLabelSocialLinksLoading,
      error: createOrUpdateWhiteLabelSocialLinksError,
    },
  ] = useCreateOrUpdateWhiteLabelSocialLinksMutation();
  const { data: whiteLabelInfo, loading: whiteLabelInfoLoading } =
    useOrganizerWhiteLabelQuery();

  const [isShowToast, setIsShowToast] = useState(false);

  const whiteLabelJRActiveValue = useReactiveVar(whiteLabelJRActive);

  useEffect(() => {
    if (whiteLabelInfo) {
      const whiteLabelConfig =
        whiteLabelInfo.organizer.whiteLabel?.whiteLabelConfig;
      formRef.current?.setValues({
        instagram: whiteLabelConfig?.instagramLink || "",
        facebook: whiteLabelConfig?.facebookLink || "",
        x: whiteLabelConfig?.twitterLink || "",
        youtube: whiteLabelConfig?.youtubeLink || "",
        linkedin: whiteLabelConfig?.linkedIn || "",
        tiktok: whiteLabelConfig?.tikTok || "",
      });
    }
  }, [whiteLabelInfo]);

  const onSubmit = async (
    values: ISocialLinksForm,
    { setSubmitting }: FormikHelpers<ISocialLinksForm>
  ) => {
    await createOrUpdateWhiteLabelSocialLinks({
      variables: {
        input: {
          facebookLink: values.facebook,
          instagramLink: values.instagram,
          linkedInLink: values.linkedin,
          tikTokLink: values.tiktok,
          twitterLink: values.x,
          youtubeLink: values.youtube,
        },
      },
      onCompleted: () => {
        setIsShowToast(true);
        if (whiteLabelJRActiveValue) {
          whiteLabelJRStep(5);
          navigate(`${lng}/custom-domain`);
        }
      },
    });
    setSubmitting(false);
  };

  const initialValues: ISocialLinksForm = {
    instagram: "",
    facebook: "",
    x: "",
    youtube: "",
    linkedin: "",
    tiktok: "",
  };

  if (whiteLabelInfoLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 200 }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchemaSocialLinks(t)}
        enableReinitialize
      >
        {({ isSubmitting, isValid, submitCount }) => {
          return (
            <FormikForm id="social-links">
              <ScrollToFieldError />
              <Row className="g-3 mb-5">
                <Col md={6}>
                  <InputText
                    name="instagram"
                    className="mb-0"
                    placeholder="Instagram"
                    prefixIcon={<InstagramIcon />}
                  />
                </Col>
                <Col md={6}>
                  <InputText
                    name="facebook"
                    className="mb-0"
                    placeholder="Facebook"
                    prefixIcon={<FacebookIcon />}
                  />
                </Col>
                <Col md={6}>
                  <InputText
                    name="x"
                    className="mb-0"
                    placeholder="X (Twitter)"
                    prefixIcon={<XIcon />}
                  />
                </Col>
                <Col md={6}>
                  <InputText
                    name="youtube"
                    className="mb-0"
                    placeholder="YouTube"
                    prefixIcon={<YoutubeIcon />}
                  />
                </Col>
                <Col md={6}>
                  <InputText
                    name="linkedin"
                    className="mb-0"
                    placeholder="LinkedIn"
                    prefixIcon={<LinkedinIcon />}
                  />
                </Col>
                <Col md={6}>
                  <InputText
                    name="tiktok"
                    className="mb-0"
                    placeholder="Tik-tok"
                    prefixIcon={<TiktokIcon />}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col sm={4}>
                  <Button
                    variant="success"
                    className="fw-bold w-100 py-2"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (submitCount > 0 && !isValid) ||
                      createOrUpdateWhiteLabelSocialLinksLoading
                    }
                  >
                    {t("firstStep.save")}
                    {createOrUpdateWhiteLabelSocialLinksLoading && (
                      <Spinner
                        animation="border"
                        role="status"
                        size="sm"
                        className="ms-2"
                      />
                    )}
                  </Button>
                </Col>
              </Row>
              <ApolloError
                networkError={
                  createOrUpdateWhiteLabelSocialLinksError?.networkError
                }
              />
              <CustomToast
                isShow={isShowToast}
                handleClose={() => setIsShowToast(false)}
                message={t("whiteLabel.Changes Saved")}
              />
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
}
